import React from 'react';
import { Box } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';

import {
  FGrid,
  FGridMissingCell,
  FGridMoneyCell,
  FGridPercentCell,
  FGridStatusChip,
  isMissingGridValue,
} from '../grid';

import { ActivityProgrammeRow } from './types';

type Props = {
  rows: ActivityProgrammeRow[];
  loading?: boolean;
  onNotifyResponsible?: (row: ActivityProgrammeRow) => void;
  onNotifyIncompleteResponsible?: (row: ActivityProgrammeRow) => void;
  onNotifyAllIncomplete?: () => void;
  onNotifyAllResponsible?: () => void;
};

const QuarterCell = (params: GridRenderCellParams) => (
  <Box sx={{ width: '100%', textAlign: 'center' }}>
    {params.value || ''}
  </Box>
);

class ProgrammeReviewGrid extends React.Component<Props> {
  incompleteCount = () =>
    (this.props.rows || []).filter((r: any) => (r.missing || []).length > 0).length;

  getColumns = (): GridColDef[] => [
    { field: 'code', headerName: 'Code', width: 135, renderCell: FGridMissingCell },
    { field: 'domaine', headerName: 'Domaine', width: 260, renderCell: FGridMissingCell },
    { field: 'resultat', headerName: 'Résultat attendu', width: 280, renderCell: FGridMissingCell },
    { field: 'indicateur', headerName: 'Indicateur', width: 280, renderCell: FGridMissingCell },
    { field: 'cible', headerName: 'Cible', width: 110, renderCell: FGridMissingCell },

    { field: 't1', headerName: 'T1', width: 70, align: 'center', headerAlign: 'center', renderCell: QuarterCell },
    { field: 't2', headerName: 'T2', width: 70, align: 'center', headerAlign: 'center', renderCell: QuarterCell },
    { field: 't3', headerName: 'T3', width: 70, align: 'center', headerAlign: 'center', renderCell: QuarterCell },
    { field: 't4', headerName: 'T4', width: 70, align: 'center', headerAlign: 'center', renderCell: QuarterCell },

    { field: 'statut', headerName: 'Statut activité', width: 150, renderCell: FGridStatusChip },
    { field: 'coutinitial', headerName: 'Coût initial', width: 150, renderCell: FGridMoneyCell },
    { field: 'coutrevise', headerName: 'Coût révisé', width: 150, renderCell: FGridMoneyCell },
    { field: 'ecartpercent', headerName: 'Écart %', width: 120, renderCell: FGridPercentCell },
    { field: 'ecartamount', headerName: 'Écart', width: 150, renderCell: FGridMoneyCell },

    { field: 'commentaires', headerName: 'Commentaires', width: 240, renderCell: FGridMissingCell },
    { field: 'responsable', headerName: 'Responsable', width: 260, renderCell: FGridMissingCell },

    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 115,
      getActions: (params) => {
        const row = params.row as ActivityProgrammeRow;
        const rowIncomplete = ((row as any).missing || []).length > 0;

        return [
          <GridActionsCellItem
            key="notify-responsable"
            icon={<SendIcon fontSize="small" />}
            label="Notify responsable"
            onClick={() => this.props.onNotifyResponsible?.(row)}
          />,
          <GridActionsCellItem
            key="notify-missing"
            icon={<WarningAmberIcon fontSize="small" />}
            label="Notify missing"
            disabled={!rowIncomplete}
            onClick={() => this.props.onNotifyIncompleteResponsible?.(row)}
          />,
        ];
      },
    },
  ];

  toolbarActions = () => {
    const incompleteCount = this.incompleteCount();
    const total = this.props.rows?.length || 0;

    return [
      {
        key: 'notify-incomplete',
        label: 'Notify Incomplete',
        title: 'Notify all responsables with incomplete programme records',
        icon: <WarningAmberIcon />,
        color: 'warning' as const,
        variant: 'contained' as const,
        disabled: incompleteCount === 0,
        onClick: () => this.props.onNotifyAllIncomplete?.(),
      },
      {
        key: 'notify-all',
        label: 'Notify All',
        title: 'Notify all responsables in the current filtered list',
        icon: <NotificationsActiveIcon />,
        color: 'primary' as const,
        variant: 'outlined' as const,
        disabled: total === 0,
        onClick: () => this.props.onNotifyAllResponsible?.(),
      },
    ];
  };

  render() {
    const { rows, loading } = this.props;
    const incompleteCount = this.incompleteCount();

    return (
      <FGrid
        title="Programme d'Activités — Horizontal Review"
        subtitle={`${rows?.length || 0} records • ${incompleteCount} incomplete`}
        rows={rows || []}
        columns={this.getColumns()}
        loading={loading}
        height={650}
        showQuickFilter
        toolbarSx={{ backgroundColor: '#bbd4e6' }}
        slotProps={{
          toolbar: {
            actions: this.toolbarActions(),
            total: rows?.length || 0,
            dirtyCount: incompleteCount,
          } as any,
        }}
        getRowId={(row: any) => row.rid || row.code}
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        getCellClassName={(params) =>
          isMissingGridValue(params.row, params.field) ? 'missing-cell' : ''
        }
        sx={{
          '& .missing-cell': {
            bgcolor: '#fdecea',
            color: '#8a1f11',
            fontWeight: 600,
          },
        }}
      />
    );
  }
}

export default ProgrammeReviewGrid;
